import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BarChart3, ChevronRight, FileSpreadsheet, FileText, Filter, Loader2, RefreshCw, X } from 'lucide-react';
import { filterGridClassName } from '../common/uiClasses.js';
import {
  AgentBreakdownTable,
  ReportCell,
  ReportRecordCard,
  TotalsSummaryTable,
  useReportFilterCatalog,
} from './useReportFilterCatalog.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { useAuth } from '../../context/AuthContext.js';
import reportService from '../../services/ReportService.js';
import { filtersFromSearch, filtersToSearch } from '../../utils/reportInsights.js';
import GstCollectionReportView from './GstCollectionReportView.js';
import ReportRecordModals from './ReportRecordModals.js';
import {
  REPORT_DEFS,
  buildCleanParams,
  canExportExcel,
  colorMap,
  getDefaultFilters,
  getTodayIso,
  isReportVisible,
} from './reportConfig.js';
import { formatFinancialCell } from '../../utils/formatCurrency.js';


const REPORT_PAGE_SIZE = 10;

function mergeReportPage(current, incoming) {
  if (!current) return incoming;
  const prevSections = current.sections || [];
  const nextSections = incoming.sections || [];
  let sections = nextSections;
  if (prevSections.length) {
    sections = prevSections.map((section) => ({ ...section, rows: [...(section.rows || [])] }));
    nextSections.forEach((section) => {
      const found = sections.find((item) => item.title === section.title && item.kind === section.kind);
      if (found) {
        found.rows.push(...(section.rows || []));
        if (section.totals_row) found.totals_row = section.totals_row;
      } else {
        sections.push(section);
      }
    });
  }
  const total = incoming.meta?.total_count ?? incoming.meta?.count ?? current.meta?.total_count;
  return {
    ...current,
    title: incoming.title || current.title,
    columns: incoming.columns?.length ? incoming.columns : current.columns,
    totals: current.totals || incoming.totals,
    totals_row: current.totals_row || incoming.totals_row,
    footer_rows: current.footer_rows || incoming.footer_rows,
    rows: [...(current.rows || []), ...(incoming.rows || [])],
    sections,
    meta: {
      ...(current.meta || {}),
      ...(incoming.meta || {}),
      count: total,
      total_count: total,
      has_more: Boolean(incoming.meta?.has_more),
    },
  };
}

const REPORT_TYPE_ALIASES = {
  dispatch: 'ml-sheet',
  ogcl: 'ml-sheet',
  ogpl: 'ml-sheet',
  'profit-loss': 'route-pl',
  pl: 'route-pl',
  delivery: 'delivery-event',
};

const ReportDetailPage = () => {
  const { type: typeParam } = useParams();
  const type = REPORT_TYPE_ALIASES[typeParam] || typeParam;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const def = REPORT_DEFS[type];
  const [filters, setFilters] = useState(() => filtersFromSearch(searchParams, getDefaultFilters(def, user)));
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState('');
  const [shipmentPreviewId, setShipmentPreviewId] = useState('');
  const [mlPreviewId, setMlPreviewId] = useState('');
  const [expandedBranches, setExpandedBranches] = useState({});

  const openShipment = (shipmentId) => setShipmentPreviewId(String(shipmentId || '').trim());
  const openMlSheet = (mlSheetId) => setMlPreviewId(String(mlSheetId || '').trim());

  const requestSeq = useRef(0);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);

  const loadData = useCallback(async (filterOverride, page = 1, append = false) => {
    if (!def) return null;
    const activeFilters = filterOverride || filters;
    const params = buildCleanParams(activeFilters);
    if ((def.type === 'daily-branch' || def.type === 'receivable') && !params.report_date) {
      params.report_date = getTodayIso();
    }
    if (def.type === 'daily-business') {
      const today = getTodayIso();
      if (!params.from_date) params.from_date = today;
      if (!params.to_date) params.to_date = today;
    }
    params.page = page;
    params.page_size = REPORT_PAGE_SIZE;
    const seq = ++requestSeq.current;
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      hasMoreRef.current = false;
    }
    setError('');
    try {
      const res = await reportService.getReportData(def.type, params);
      if (seq !== requestSeq.current) return null;
      if (res?.success === false) {
        setError(res.error || 'Failed to load report');
        if (!append) setPayload(null);
        hasMoreRef.current = false;
        return null;
      }
      const data = res?.data || res;
      pageRef.current = page;
      hasMoreRef.current = Boolean(data?.meta?.has_more);
      setPayload((prev) => (append ? mergeReportPage(prev, data) : data));
      return params;
    } catch (err) {
      if (seq !== requestSeq.current) return null;
      console.error(err);
      setError(err?.response?.data?.error || err.message || 'Failed to load report');
      if (!append) setPayload(null);
      hasMoreRef.current = false;
      return null;
    } finally {
      if (seq === requestSeq.current) {
        if (append) setLoadingMore(false);
        else setLoading(false);
        loadingMoreRef.current = false;
      }
    }
  }, [def, filters]);

  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    loadData(filters, pageRef.current + 1, true);
  }, [filters, loadData]);

  // Reset filters when navigating between report types
  useEffect(() => {
    if (!def || !isReportVisible(def, user)) return;
    const defaults = filtersFromSearch(searchParams, getDefaultFilters(def, user));
    setFilters(defaults);
    setPayload(null);
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);


  // Dynamically reload table whenever filters change (debounced)
  useEffect(() => {
    if (!def || !isReportVisible(def, user)) return undefined;
    const timer = setTimeout(() => {
      pageRef.current = 1;
      loadData(filters, 1, false);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, type, def]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || loading || !payload?.meta?.has_more) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadMore();
    }, { root: null, rootMargin: '240px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, loading, loadingMore, payload]);

  const filterFields = useReportFilterCatalog({ type, user, filters, setFilters });

  if (!def) {
    return (
      <DashboardLayout>
        <p>Unknown report type.</p>
        <button type="button" onClick={() => navigate('/reports')}>Back</button>
      </DashboardLayout>
    );
  }

  if (!isReportVisible(def, user)) {
    return (
      <DashboardLayout>
        <p>You do not have access to this report.</p>
        <button type="button" onClick={() => navigate('/reports')}>Back</button>
      </DashboardLayout>
    );
  }

  const c = colorMap[def.color] || colorMap.indigo;
  const Icon = def.icon;
  const columns = payload?.columns || [];
  const rows = payload?.rows || [];
  const totals = payload?.totals;
  const sections = payload?.sections || [];
  const isStatement = (payload?.layout === 'statement') || def.layout === 'statement' || sections.length > 0;
  const allowExcel = canExportExcel(user);

  const handleExcel = async () => {
    setExcelLoading(true);
    try {
      // Ensure export matches current filters (refresh table first if needed)
      const params = buildCleanParams(filters);
      if ((def.type === 'daily-branch' || def.type === 'receivable') && !params.report_date) {
        params.report_date = getTodayIso();
      }
      if (def.type === 'daily-business') {
        const today = getTodayIso();
        if (!params.from_date) params.from_date = today;
        if (!params.to_date) params.to_date = today;
      }
      await loadData(params);
      await reportService.downloadExcel(def.type, params);
    } catch (err) {
      alert(err?.response?.data?.error || 'Excel download failed');
    } finally {
      setExcelLoading(false);
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const params = buildCleanParams(filters);
      if ((def.type === 'daily-branch' || def.type === 'receivable') && !params.report_date) {
        params.report_date = getTodayIso();
      }
      if (def.type === 'daily-business') {
        const today = getTodayIso();
        if (!params.from_date) params.from_date = today;
        if (!params.to_date) params.to_date = today;
      }
      await loadData(params);
      await reportService.downloadPdf(def.type, params);
    } catch (err) {
      alert(err?.response?.data?.error || 'PDF download failed');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div className="w-full max-w-full min-w-0">
        {/* Header: title + Excel / PDF / Back */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3 sm:mb-4">
          <div className="flex items-start gap-2.5 sm:gap-3 min-w-0 flex-1">
            <div
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: c.iconBg, border: `1px solid ${c.buttonBorder}` }}
            >
              <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: c.iconColor }} />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-extrabold text-slate-900 m-0 leading-tight truncate">{def.title}</h1>
              <p className="text-[11px] text-slate-400 mt-0.5 m-0 leading-snug line-clamp-1 sm:line-clamp-2">{def.description}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0 lg:justify-end">
            <button
              type="button"
              onClick={() => {
                const params = buildCleanParams(filters);
                if ((def.type === 'daily-branch' || def.type === 'receivable') && !params.report_date) {
                  params.report_date = getTodayIso();
                }
                if (def.type === 'daily-business') {
                  const today = getTodayIso();
                  if (!params.from_date) params.from_date = today;
                  if (!params.to_date) params.to_date = today;
                }
                const qs = filtersToSearch(params);
                navigate(qs ? `/reports/${def.type}/insights?${qs}` : `/reports/${def.type}/insights`);
              }}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold"
              style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', color: '#6d28d9' }}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              Insights
            </button>
            {allowExcel && (
              <button
                type="button"
                onClick={handleExcel}
                disabled={excelLoading}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold disabled:opacity-60"
                style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' }}
              >
                {excelLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
                Excel
              </button>
            )}
            <button
              type="button"
              onClick={handlePdf}
              disabled={pdfLoading}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-semibold disabled:opacity-60"
              style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}
            >
              {pdfLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              PDF
            </button>
            <button
              type="button"
              onClick={() => navigate('/reports')}
              className="inline-flex items-center gap-1.5 shrink-0 border border-slate-200 bg-white rounded-lg px-2.5 py-2 text-xs font-semibold text-slate-600"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          </div>
        </div>

        {/* Filters + Clear / Refresh */}
        <div
          className="rounded-xl border border-indigo-100 p-3 sm:p-4 mb-3 sm:mb-4"
          style={{ background: 'linear-gradient(135deg, #ffffff 0%, #eef2ff 100%)' }}
        >
          <div className="flex items-center gap-2 mb-2.5 sm:mb-3 flex-wrap">
            <Filter className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
            <span className="text-sm font-semibold text-slate-800">Filters</span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium hidden sm:inline">
              {type === 'daily-branch'
                ? 'One day + FY cumulative'
                : type === 'daily-business'
                  ? 'Today by default'
                  : 'Empty dates = all'}
            </span>
            <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={() => {
                  const defaults = getDefaultFilters(def, user);
                  setFilters(defaults);
                  loadData(defaults);
                }}
                title="Clear filters"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold border border-red-200 bg-white text-red-500 hover:bg-red-50"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                Clear
              </button>
              <button
                type="button"
                onClick={() => loadData()}
                disabled={loading}
                title="Apply / Refresh"
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-semibold disabled:opacity-60"
                style={{ background: c.buttonBg, border: `1px solid ${c.buttonBorder}`, color: c.buttonText }}
              >
                {loading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.25} />}
                Refresh
              </button>
            </div>
          </div>
          <div className={`${filterGridClassName} [&>*]:min-w-0`}>
            {filterFields}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-[10px] p-3 mb-4 text-xs sm:text-[13px]">
            {error}
          </div>
        )}

        {/* Results */}
        {type === 'gst-collection' ? (
          <>
            <p className="mb-3 text-[11px] text-amber-800">
              A date range above 7 days downloads Excel instead of listing the consignments. State-wise totals and the summary still show on this page.
            </p>
            <GstCollectionReportView payload={payload} loading={loading} loadingMore={loadingMore} />
          </>
        ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
            <span className="text-xs sm:text-[13px] font-bold text-slate-900 truncate">{payload?.title || def.title}</span>
            <span className="text-[11px] sm:text-xs text-slate-500">
              {loading
                ? 'Loading report…'
                : `${isStatement
                  ? sections.reduce((n, s) => n + (s.rows || []).length, 0)
                  : rows.length} of ${payload?.meta?.total_count ?? payload?.meta?.count ?? rows.length} record(s)`}
            </span>
          </div>
          {!loading && (
            <>
              <TotalsSummaryTable
                totals={totals}
                recordCount={type === 'daily-business'
                  ? null
                  : (payload?.meta?.total_count ?? payload?.meta?.count ?? rows.length)}
              />
              <AgentBreakdownTable breakdown={payload?.meta?.agent_breakdown || payload?.agent_breakdown} />
            </>
          )}

          {payload?.meta?.warning && (
            <div className="mx-3 mt-3 mb-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {payload.meta.warning}
            </div>
          )}

          {isStatement ? (
            <div className="p-2.5 sm:p-4 space-y-4">
              {loading && (
                <div className="text-center text-slate-400 text-sm py-8">Loading report…</div>
              )}
              {!loading && sections.length === 0 && (
                <div className="text-center text-slate-400 text-sm py-8">No data for the selected filters.</div>
              )}
              {!loading && sections.map((section, sidx) => {
                const cols = section.columns || columns;
                const srows = section.rows || [];
                const rowMeta = section.row_meta || [];
                return (
                  <div key={sidx} className="rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-3 py-2 text-xs font-bold text-slate-800" style={{ background: '#B4C7E7' }}>
                      {section.title}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-xs min-w-[480px]">
                        <thead>
                          <tr>
                            {cols.map((col) => (
                              <th
                                key={col}
                                className="text-left px-3 py-2 whitespace-nowrap font-bold border-b border-sky-300"
                                style={{ background: '#D6E3F8', color: '#1F4E79' }}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {srows.map((row, idx) => {
                            const meta = rowMeta[idx] || {};
                            if (meta.kind === 'agent' && !expandedBranches[`${sidx}:${meta.parent}`]) return null;
                            const highlight = meta.is_nrr || section.highlight === 'total_nrr';
                            const cells = Array.isArray(row) ? row : cols.map((col) => row[col]);
                            const expanded = Boolean(expandedBranches[`${sidx}:${meta.key}`]);
                            return (
                              <tr
                                key={idx}
                                style={{
                                  background: meta.kind === 'agent'
                                    ? '#F8FAFC'
                                    : highlight
                                      ? (section.highlight === 'total_nrr' ? '#C6EFCE' : '#D9E1F2')
                                      : (idx % 2 ? '#F8FAFC' : '#fff'),
                                  fontWeight: highlight ? 700 : 400,
                                }}
                              >
                                {cells.map((cell, cidx) => (
                                  <td key={cidx} className={`px-3 py-1.5 border-b border-slate-200 whitespace-nowrap ${meta.kind === 'agent' && cidx === 0 ? 'pl-8 text-slate-600' : ''}`}>
                                    {cidx === 0 && meta.has_children ? (
                                      <button
                                        type="button"
                                        className="inline-flex items-center gap-1 font-semibold text-slate-800"
                                        onClick={() => setExpandedBranches((prev) => ({
                                          ...prev,
                                          [`${sidx}:${meta.key}`]: !prev[`${sidx}:${meta.key}`],
                                        }))}
                                      >
                                        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                                        <ReportCell col={cols[cidx]} cell={cell} onOpenShipment={openShipment} onOpenMlSheet={openMlSheet} />
                                      </button>
                                    ) : (
                                      <ReportCell col={cols[cidx]} cell={cell} onOpenShipment={openShipment} onOpenMlSheet={openMlSheet} />
                                    )}
                                  </td>
                                ))}
                              </tr>
                            );
                          })}
                          {section.totals_row && (
                            <tr style={{ background: '#B4C7E7', fontWeight: 700 }}>
                              {(section.totals_row || []).map((cell, cidx) => (
                                <td key={cidx} className="px-3 py-2 border-b border-sky-300 whitespace-nowrap">
                                  {cell == null ? '' : (formatFinancialCell(cols[cidx], cell) ?? '')}
                                </td>
                              ))}
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {/* Phone / iPad: compact 2-field cards */}
              <div className="lg:hidden p-2.5 sm:p-3 space-y-2">
                {loading && (
                  <div className="text-center text-slate-400 text-sm py-8">Loading report…</div>
                )}
                {!loading && rows.length === 0 && (
                  <div className="text-center text-slate-400 text-sm py-8">No data for the selected filters.</div>
                )}
                {!loading && rows.map((row, idx) => {
                  const cells = Array.isArray(row) ? row : columns.map((col) => row[col]);
                  return (
                    <ReportRecordCard
                      key={idx}
                      columns={columns}
                      cells={cells}
                      onOpenShipment={openShipment}
                      onOpenMlSheet={openMlSheet}
                    />
                  );
                })}
                {!loading && (payload?.totals_row || (payload?.footer_rows || []).length > 0) && (
                  <div className="rounded-lg border border-sky-200 bg-sky-50 p-2.5 space-y-2">
                    {[payload?.totals_row, ...(payload?.footer_rows || [])].filter(Boolean).map((footerRow, fidx) => (
                      <div key={`m-footer-${fidx}`} className="text-xs">
                        <p className="font-bold text-slate-800">{footerRow[0] || 'TOTAL'}</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1">
                          {columns.map((col, cidx) => (
                            cidx > 0 && footerRow[cidx] !== '' && footerRow[cidx] != null ? (
                              <div key={col}>
                                <span className="text-[10px] text-slate-500">{col}: </span>
                                <span className="font-semibold">
                                  {formatFinancialCell(col, footerRow[cidx]) ?? footerRow[cidx]}
                                </span>
                              </div>
                            ) : null
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Desktop: table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full border-collapse text-xs min-w-[640px]">
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th
                          key={col}
                          className="text-left px-3 py-2.5 whitespace-nowrap font-bold border-b border-sky-300"
                          style={{ background: '#B4C7E7', color: '#1F4E79' }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && rows.length === 0 && (
                      <tr>
                        <td colSpan={Math.max(columns.length, 1)} className="px-6 py-8 text-center text-slate-400">
                          No data for the selected filters.
                        </td>
                      </tr>
                    )}
                    {rows.map((row, idx) => (
                      <tr key={idx} style={{ background: idx % 2 ? '#F2F7FF' : '#fff' }}>
                        {(Array.isArray(row) ? row : columns.map((col) => row[col])).map((cell, cidx) => (
                          <td key={cidx} className="px-3 py-2 border-b border-slate-200 whitespace-nowrap">
                            <ReportCell col={columns[cidx]} cell={cell} onOpenShipment={openShipment} onOpenMlSheet={openMlSheet} />
                          </td>
                        ))}
                      </tr>
                    ))}
                    {payload?.totals_row && (
                      <tr style={{ background: '#B4C7E7', fontWeight: 700 }}>
                        {(payload.totals_row || []).map((cell, cidx) => (
                          <td key={cidx} className="px-3 py-2 border-b border-sky-300 whitespace-nowrap">
                            {cell == null ? '' : (formatFinancialCell(columns[cidx], cell) ?? '')}
                          </td>
                        ))}
                      </tr>
                    )}
                    {(payload?.footer_rows || []).map((footerRow, fidx) => (
                      <tr key={`footer-${fidx}`} style={{ background: fidx === (payload.footer_rows || []).length - 1 ? '#FFF2CC' : '#B4C7E7', fontWeight: 700 }}>
                        {(footerRow || []).map((cell, cidx) => (
                          <td key={cidx} className="px-3 py-2 border-b border-sky-300 whitespace-nowrap">
                            {cell == null ? '' : (formatFinancialCell(columns[cidx], cell) ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!loading && (loadingMore || payload?.meta?.has_more) && (
            <div ref={sentinelRef} className="px-3 py-3 text-center text-xs text-slate-500">
              {loadingMore ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading more records…
                </span>
              ) : null}
            </div>
          )}
        </div>
        )}
        {type === 'gst-collection' && !loading && (loadingMore || payload?.meta?.has_more) && (
          <div ref={sentinelRef} className="px-3 py-3 text-center text-xs text-slate-500">
            {loadingMore ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading more records…
              </span>
            ) : null}
          </div>
        )}
        <ReportRecordModals
          shipmentId={shipmentPreviewId}
          mlSheetId={mlPreviewId}
          onCloseShipment={() => {
            setShipmentPreviewId('');
            setMlPreviewId('');
          }}
          onCloseMlSheet={() => setMlPreviewId('')}
          onOpenMlSheet={openMlSheet}
          onOpenShipment={openShipment}
        />
      </div>
    </DashboardLayout>
  );
};

export default ReportDetailPage;
